import { z } from 'zod';
import { CancerStatus, SignUpMethod } from '../enums';
import { dateValidation, emailValidation } from '../../validation';

// The patient of a case, as the platform creates it (POST /cases) and as the
// platform or the EBP UI updates it (PUT /cases/:id).
const CreateEbpPatientFieldsSchema = z.object({
  fhirId: z.string().nullish(),
  shortId: z.string().nullish(),
  email: emailValidation().optional(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  // The date of birth, under the name everything else that carries one
  // already uses: the referral, the care documents, the booking link
  dob: dateValidation().nullish(),
  /**
   * @deprecated Renamed to `dob`. Still accepted from a caller that has not
   * moved yet (the platform's `EbpCreatePatientSchema`), read into `dob` while
   * parsing and never stored: a stored patient has `dob` only (migration
   * v0.0.110). When a body sends both, `dob` wins.
   */
  dateOfBirth: dateValidation().nullish(),
  cancerStatus: z.enum(CancerStatus).nullish(),

  policyNumber: z.string().nullish(),
  // The partner's claim reference of the member's latest referral, sent by
  // the platform so the Cases search can match it (PPL-4223)
  claimNumber: z.string().nullish(),
  client: z.string().nullish(),
  // Patient identifier assigned by the referring organization
  // (referral patient.externalId, stored in the platform as the
  // Descope User tenantUserId attribute)
  tenantUserId: z.string().nullish(),
  // How the member's account on the platform is set up (SignUpMethod). A
  // value this EBP does not know yet is dropped rather than failing the case
  // or the update it came with: the platform may add one first.
  signUpMethod: z.enum(SignUpMethod).nullish().catch(undefined),
});

// Update from the platform PUT request or from the EBP UI
const UpdateEbpPatientFieldsSchema =
  // All CreateEbpPatientSchema can be updated by the platform
  CreateEbpPatientFieldsSchema.extend({
    // The following fields can be updated from the EBP UI
    level: z.string().max(30).optional(),
  });

// Folds the deprecated name into `dob` and drops it, so what a schema hands
// out never carries `dateOfBirth`: the readers and the store know `dob` only.
// `dob` wins when a body states both — a caller half way through the rename
// is never overridden by the name it has already left behind.
const readDeprecatedDateOfBirth = <
  T extends { dob?: Date | null; dateOfBirth?: Date | null },
>({
  dateOfBirth,
  ...patient
}: T): Omit<T, 'dateOfBirth'> => {
  if (patient.dob === undefined && dateOfBirth !== undefined) {
    return { ...patient, dob: dateOfBirth };
  }
  return patient;
};

export const CreateEbpPatientSchema = CreateEbpPatientFieldsSchema.transform(
  readDeprecatedDateOfBirth,
);

export const UpdateEbpPatientSchema = UpdateEbpPatientFieldsSchema.transform(
  readDeprecatedDateOfBirth,
);

export const EbpPatientSchema = UpdateEbpPatientSchema;
